import {
  KB_SOURCE_KINDS,
  KB_TITLE_MAX_CHARS,
  KB_UPLOAD_CONTENT_TYPES,
  KB_UPLOAD_MAX_BYTES,
  UploadKbSourceForm,
  type KbSourceKind,
} from '@rp/contracts'
import { useState } from 'react'
import { Button, Card, Field } from '../../design'
import { KB_COPY } from './copy'
import type { AddKbSourceInput } from './queries'

type UploadProblem = { title: string | null; file: string | null }

const NO_UPLOAD_PROBLEM: UploadProblem = { title: null, file: null }

const fileMessageOf = (issue: { path: readonly PropertyKey[]; code: string }): string => {
  if (issue.path[0] === 'contentType') return KB_COPY.unsupportedFile
  return issue.code === 'too_big' ? KB_COPY.fileTooLarge : KB_COPY.emptyFile
}

const uploadProblemOf = (title: string, file: File): UploadProblem => {
  const parsed = UploadKbSourceForm.safeParse({ title, contentType: file.type, sizeBytes: file.size })
  if (parsed.success) return NO_UPLOAD_PROBLEM
  const titleIssue = parsed.error.issues.find((issue) => issue.path[0] === 'title')
  const fileIssue = parsed.error.issues.find((issue) => issue.path[0] !== 'title')
  return {
    title: titleIssue === undefined ? null : KB_COPY.titleTooLong,
    file: fileIssue === undefined ? null : fileMessageOf(fileIssue),
  }
}

const KIND_LABEL_ID = 'kb-kind-label'

const KindChoice = ({ kind, onChoose }: { kind: KbSourceKind; onChoose: (next: KbSourceKind) => void }) => (
  <div className="kb-kind-field">
    <span className="kb-kind-label" id={KIND_LABEL_ID}>
      {KB_COPY.kind}
    </span>
    <div className="kb-kind" role="group" aria-labelledby={KIND_LABEL_ID}>
      {KB_SOURCE_KINDS.map((value) => (
        <Button key={value} className="kb-kind-option" aria-pressed={kind === value} onClick={() => onChoose(value)}>
          {KB_COPY.kinds[value]}
        </Button>
      ))}
    </div>
  </div>
)

export const AddSourceForm = ({
  onSubmit,
  pending,
  error,
}: {
  onSubmit: (input: AddKbSourceInput) => void
  pending: boolean
  error: string | null
}) => {
  const [kind, setKind] = useState<KbSourceKind>('upload')
  const [title, setTitle] = useState('')
  const [uri, setUri] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [problem, setProblem] = useState<UploadProblem>(NO_UPLOAD_PROBLEM)
  const missingSource = kind === 'url' ? uri.trim() === '' : file === null
  const invalid = title.trim() === '' || missingSource

  const submitUpload = (chosen: File) => {
    const found = uploadProblemOf(title.trim(), chosen)
    setProblem(found)
    if (found.title === null && found.file === null) onSubmit({ kind: 'upload', title: title.trim(), file: chosen })
  }

  const submit = () => {
    if (kind === 'url') {
      onSubmit({ kind, title: title.trim(), uri: uri.trim() })
      return
    }
    if (file !== null) submitUpload(file)
  }

  const chooseKind = (next: KbSourceKind) => {
    setKind(next)
    setProblem(NO_UPLOAD_PROBLEM)
  }

  const chooseFile = (chosen: File | null) => {
    setFile(chosen)
    setProblem(NO_UPLOAD_PROBLEM)
  }

  return (
    <Card as="section" className="kb-add">
      <h2>{KB_COPY.addTitle}</h2>
      <KindChoice kind={kind} onChoose={chooseKind} />
      <Field id="kb-title" label={KB_COPY.sourceTitle} {...(problem.title === null ? {} : { error: problem.title })}>
        {({ id, describedBy }) => (
          <input
            id={id}
            maxLength={KB_TITLE_MAX_CHARS}
            aria-describedby={describedBy}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}
      </Field>
      {kind === 'url' ? (
        <Field id="kb-uri" label={KB_COPY.uri}>
          {({ id }) => <input id={id} type="url" inputMode="url" value={uri} onChange={(event) => setUri(event.target.value)} />}
        </Field>
      ) : (
        <Field
          id="kb-file"
          label={KB_COPY.file}
          hint={KB_COPY.fileHint(KB_UPLOAD_MAX_BYTES)}
          {...(problem.file === null ? {} : { error: problem.file })}
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              type="file"
              accept={KB_UPLOAD_CONTENT_TYPES.join(',')}
              aria-describedby={describedBy}
              onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
            />
          )}
        </Field>
      )}
      <p className="kb-warning tone-warning">{KB_COPY.retireWarning}</p>
      {error === null ? null : (
        <p className="tone-danger kb-add-error" role="alert">
          {error}
        </p>
      )}
      <Button
        variant="primary"
        size="large"
        disabled={invalid}
        pending={pending}
        pendingLabel={KB_COPY.submitting}
        onClick={submit}
      >
        {KB_COPY.submit}
      </Button>
    </Card>
  )
}
